import { Controller } from "@hotwired/stimulus"
import { readStripeAppearance, observeStripeTheme } from "../utils/stripe_appearance"

export default class extends Controller {
  static targets = ["card", "errors", "submit"]
  static values = { 
    publishableKey: String, 
    insurancePolicyId: String,
    successUrl: String,
    customerEmail: String,
    paymentMethodTypes: String,
    stripeAccountId: String,
    billingCountry: String
  }

  connect() {
    // Listen for form reset events
    this.resetFormHandler = this.resetForm.bind(this)
    document.addEventListener('stripe:resetForm', this.resetFormHandler)

    if (!this.publishableKeyValue) {
      console.error('No Stripe publishable key provided')
      setTimeout(() => {
        if (this.hasErrorsTarget) {
          this.displayError({ message: 'Configuration Stripe manquante' })
        }
      }, 100)
      return
    }
    
    this.unobserveTheme = observeStripeTheme(() => this.updateAppearance())

    // Wait for Stripe.js to be loaded
    this.waitForStripe()
  }

  waitForStripe() {
    if (typeof Stripe === 'undefined') {
      console.log('Waiting for Stripe.js to load...')
      setTimeout(() => this.waitForStripe(), 100)
      return
    }
    
    console.log('Stripe.js loaded, initializing...')
    try {
      if (this.stripeAccountIdValue) {
        this.stripe = Stripe(this.publishableKeyValue, { stripeAccount: this.stripeAccountIdValue })  // Connect account context
      } else {
        this.stripe = Stripe(this.publishableKeyValue)  // Main account context
      }
      console.log('Stripe instance created successfully')
      this.initializeSetupIntent()
    } catch (error) {
      console.error('Error creating Stripe instance:', error)
    }
  }

  disconnect() {
    if (this.cardElement) {
      this.cardElement.destroy()
    }
    if (this.elements) {
      this.elements = null
    }
    // Remove event listener
    if (this.resetFormHandler) {
      document.removeEventListener('stripe:resetForm', this.resetFormHandler)
    }
    if (this.unobserveTheme) {
      this.unobserveTheme()
    }
  }

  // Helper method to get CSRF token with fallbacks
  getCSRFToken() {
    // Try to get CSRF token from meta tag first
    const csrfMeta = document.querySelector('[name="csrf-token"]')
    if (csrfMeta && csrfMeta.content) {
      return csrfMeta.content
    }
    
    // Fallback: try to get from Rails.csrfToken() if available
    if (typeof Rails !== 'undefined' && Rails.csrfToken) {
      return Rails.csrfToken()
    }
    
    // Fallback: try to get from form authenticity token
    const authenticityTokenInput = document.querySelector('input[name="authenticity_token"]')
    if (authenticityTokenInput && authenticityTokenInput.value) {
      return authenticityTokenInput.value
    }
    
    // Last resort: return empty string (may cause 422 error but won't crash)
    console.warn('CSRF token not found in Stripe Elements controller. This may be due to test mode or embedded session.')
    return ''
  }

  updateAppearance() {
    if (!this.elements) return
    this.elements.update({ appearance: readStripeAppearance() })
  }

  async initializeSetupIntent() {
    console.log('Initializing setup intent...')
    console.log('URL will be:', `/insurance_policies/${this.insurancePolicyIdValue}/create_setup_intent`)
    
    const csrfToken = this.getCSRFToken()
    console.log('CSRF token value:', csrfToken ? 'Found' : 'Not found')
    console.log('Stripe account ID:', this.stripeAccountIdValue || 'Not set (using main account)')
    try {
      const response = await fetch(`/insurance_policies/${this.insurancePolicyIdValue}/create_setup_intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        }
      })

      if (!response.ok) {
        let errorMessage = `Failed to create setup intent: ${response.status}`
        try {
          const payload = await response.json()
          errorMessage = payload.error || errorMessage
        } catch (_) {
          errorMessage = `${errorMessage} - ${await response.text()}`
        }
        throw new Error(errorMessage)
      }

      const data = await response.json()
      this.clientSecret = data.client_secret
      this.setupIntentId = data.setup_intent_id
      
      console.log('Setup intent created successfully, setting up elements')
      this.setupElements()
    } catch (error) {
      console.error('Setup intent creation failed:', error)
      const message = error.message && !error.message.startsWith('Failed to create setup intent')
        ? error.message
        : 'Erreur lors de l\'initialisation du formulaire de paiement.'
      this.displayError({ message })
    }
  }

  setupElements() {
    if (!this.clientSecret) {
      console.error('Client secret not available, cannot setup elements')
      return
    }

    console.log('Setting up Stripe Elements with client secret')
    
    this.elements = this.stripe.elements({
      clientSecret: this.clientSecret,
      fonts: [{
        cssSrc: "https://fonts.googleapis.com/css2?family=Nunito"
      }],
      appearance: readStripeAppearance()
    })

    const paymentMethodTypes = JSON.parse(this.paymentMethodTypesValue || '["sepa_debit", "card"]')
    const billingCountry = this.hasBillingCountryValue && this.billingCountryValue ? this.billingCountryValue : 'auto'

    this.cardElement = this.elements.create('payment', {
      paymentMethodTypes: paymentMethodTypes,
      paymentMethodOrder: paymentMethodTypes,
      fields: {
        billingDetails: {
          name: 'auto',
          email: 'never',
          address: {
            country: billingCountry,
            postalCode: 'auto'
          }
        }
      }
    })

    this.cardElement.mount(this.cardTarget)

    this.cardElement.on('ready', () => {
      this.cardTarget.setAttribute('data-stripe-payment-ready', 'true')
      this.toggleSubmitButton(true)
    })

    this.cardElement.on('change', ({ error }) => {
      this.displayError(error)
      this.toggleSubmitButton(!error && this.isFormValid())
    })
  }

  async submit(event) {
    event.preventDefault()
    
    if (!this.isFormValid()) {
      return
    }

    this.setLoading(true)
    
    try {
      const customerEmail = this.getCustomerEmail()
      console.log('Confirming setup intent with email:', customerEmail)
      console.log('Customer email value from data attribute:', this.customerEmailValue)
      
      const { setupIntent, error } = await this.stripe.confirmSetup({
        elements: this.elements,
        confirmParams: {
          return_url: this.successUrlValue,
          payment_method_data: {
            billing_details: {
              email: customerEmail
            }
          }
        },
        redirect: 'if_required'
      })

      if (error) {
        console.error('Setup confirmation error:', error)
        this.displayError(error)
        this.setLoading(false)
      } else if (setupIntent.status === 'succeeded') {
        console.log('Setup intent succeeded:')
        console.log('Full setupIntent object:', setupIntent)
        console.log('Payment method object:', setupIntent.payment_method)
        
        const paymentMethodId = setupIntent.payment_method?.id || setupIntent.payment_method
        console.log('Extracted payment method ID:', paymentMethodId)
        
        if (!paymentMethodId) {
          throw new Error('No payment method ID found in setup intent')
        }
        
        // Update the insurance policy with the new payment method
        await this.updateInsurancePolicyPaymentMethod(paymentMethodId)
        
        // Direct approach - call the refresh function directly if available
        console.log('Attempting to refresh payment methods with paymentMethodId:', paymentMethodId)
        
        if (typeof window.refreshPaymentMethodsList === 'function') {
          console.log('Found global refreshPaymentMethodsList function, calling it directly')
          window.refreshPaymentMethodsList(paymentMethodId)
        } else {
          console.log('Global refreshPaymentMethodsList not found, trying event approach')
          
          // Fallback to event approach
          const paymentMethodAddedEvent = new CustomEvent('stripe:paymentMethodAdded', {
            detail: { paymentMethodId: paymentMethodId }
          })
          console.log('Event object created:', paymentMethodAddedEvent)
          document.dispatchEvent(paymentMethodAddedEvent)
          console.log('Event dispatched as fallback')
        }
        
        // Show success message
        this.showSuccessMessage('Moyen de paiement enregistré avec succès !')
      } else {
        console.error('Setup intent status:', setupIntent.status)
        this.displayError({ message: 'Le paiement n\'a pas pu être configuré.' })
        this.setLoading(false)
      }
    } catch (error) {
      console.error('Setup intent confirmation failed:', error)
      this.displayError({
        message: error.message || 'Une erreur est survenue lors de l\'enregistrement du moyen de paiement.'
      })
      this.setLoading(false)
    }
  }

  async updateInsurancePolicyPaymentMethod(paymentMethodId) {
    try {
      console.log('Updating insurance policy with payment method:', paymentMethodId)
      const response = await fetch(`/insurance_policies/${this.insurancePolicyIdValue}/update_payment_method`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken()
        },
        body: JSON.stringify({
          payment_method_id: paymentMethodId
        })
      })

      if (!response.ok) {
        let errorMessage = `Failed to update payment method: ${response.status}`
        try {
          const payload = await response.json()
          errorMessage = payload.error || errorMessage
        } catch (_) {
          errorMessage = `${errorMessage} - ${await response.text()}`
        }
        throw new Error(errorMessage)
      }
      
      const result = await response.json()
      console.log('Payment method update result:', result)
      return result
    } catch (error) {
      console.error('Failed to update insurance policy payment method:', error)
      throw error
    }
  }

  isFormValid() {
    // Add any additional form validation logic here
    return true
  }

  displayError(error) {
    console.log('Displaying error:', error)
    const errorElement = this.errorsTarget
    console.log('Error element:', errorElement)
    
    if (error) {
      errorElement.textContent = error.message
      errorElement.style.display = 'block'
    } else {
      errorElement.style.display = 'none'
    }
  }

  toggleSubmitButton(enabled) {
    this.submitTarget.disabled = !enabled
    if (enabled) {
      this.submitTarget.classList.remove('disabled')
    } else {
      this.submitTarget.classList.add('disabled')
    }
  }

  setLoading(loading) {
    if (loading) {
      this.submitTarget.disabled = true
      this.submitTarget.classList.add('disabled')
      this.submitTarget.innerHTML = '<span class="spinner-border spinner-border-sm me-2"></span>Enregistrement...'
    } else {
      this.submitTarget.disabled = false
      this.submitTarget.classList.remove('disabled')
      this.submitTarget.innerHTML = 'Enregistrer le moyen de paiement'
    }
  }

  async selectExistingPaymentMethod(event) {
    const paymentMethodId = event.target.value
    
    try {
      const response = await fetch(`/insurance_policies/${this.insurancePolicyIdValue}/update_payment_method`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken()
        },
        body: JSON.stringify({
          payment_method_id: paymentMethodId
        })
      })

      if (!response.ok) {
        throw new Error('Failed to update payment method')
      }

      // Show success message or update UI
      console.log('Payment method updated successfully')
      this.enableSubscribeButton()
      
    } catch (error) {
      console.error('Failed to update payment method:', error)
      this.displayError({ message: 'Erreur lors de la sélection du moyen de paiement.' })
    }
  }
  
  enableSubscribeButton() {
    const subscribeBtn = document.querySelector('input[value="Souscrire"]')
    if (subscribeBtn) {
      subscribeBtn.disabled = false
      subscribeBtn.classList.remove('disabled')
      subscribeBtn.title = ''
    }
  }
  
  getCustomerEmail() {
    // Customer email should always be provided from the backend
    if (!this.customerEmailValue) {
      console.error('No customer email provided - this should not happen')
      throw new Error('Customer email is required for payment method creation')
    }
    return this.customerEmailValue
  }
  
  showSuccessMessage(message) {
    // Create or update success message
    let successElement = document.getElementById('payment-success-message')
    if (!successElement) {
      successElement = document.createElement('div')
      successElement.id = 'payment-success-message'
      successElement.className = 'alert alert-success'
      this.element.prepend(successElement)
    }
    successElement.textContent = message
    successElement.style.display = 'block'
  }
  
  hidePaymentForm() {
    // Hide the payment form since it's no longer needed
    const paymentForm = this.element.querySelector('.new-payment-method-form')
    if (paymentForm) {
      paymentForm.style.display = 'none'
    }
  }
  
  resetForm() {
    console.log('Stripe form reset requested')
    
    try {
      // Clear any error messages
      this.displayError(null)
      
      // Reset the button state
      this.setLoading(false)
      
      if (this.hasCardTarget) {
        this.cardTarget.removeAttribute('data-stripe-payment-ready')
      }

      // Destroy existing elements
      if (this.cardElement) {
        this.cardElement.destroy()
        this.cardElement = null
      }
      if (this.elements) {
        this.elements = null
      }
      
      // Clear setup intent data to force recreation
      this.clientSecret = null
      this.setupIntentId = null
      
      console.log('Stripe elements destroyed, reinitializing...')
      
      // Re-initialize with fresh setup intent
      setTimeout(() => {
        this.initializeSetupIntent()
      }, 100)
      
    } catch (error) {
      console.error('Error resetting Stripe form:', error)
    }
  }
}
